import { Injectable } from "@angular/core";
import { MatSnackBar, MatSnackBarRef } from "@angular/material/snack-bar";
import { take } from "rxjs/operators";
import { SnackbarCustomComponent } from "./snackbar-custom.component";
import { SnackbarType } from "./snackbar.models";
import { SnackbarNewComponent } from "../snackbar-new/snackbar-new.component";
import { SnackbarNewData } from "../snackbar-new/snackbar-new.models";
import { OnboardingSnackbarComponent } from "../onboarding-snackbar/onboarding-snackbar.component";
import { OnboardingSnackbarData, OnboardingSnackbarOptions } from "../onboarding-snackbar/onboarding-snackbar.models";
import { BlockingUiSignalService } from "../../services/blocking-ui-signal.service";
@Injectable({
  providedIn: "root",
})
export class SnackbarService {
  /** The latest snackbar opened through this service, until it's dismissed. */
  private currentRef: MatSnackBarRef<unknown> | null = null;
  /** Onboarding snackbars waiting (FIFO) for the screen to clear - see openOnboardingSnackbar(). */
  private readonly onboardingQueue: Array<() => void> = [];
  /** The open snackbar the queue is currently waiting on, if any. */
  private waitingOnRef: MatSnackBarRef<unknown> | null = null;

  constructor(
    private snackBar: MatSnackBar,
    private blockingUiSignal: BlockingUiSignalService
  ) {}

  /**
   * New-UI (BCAD-529) snackbar — Ember→Mulberry card with a heading + body and
   * a Doodat illustration.
   */
  openDesignSnackbar(
    heading: string,
    message: string,
    duration: number = 5000
  ) {
    return this.trackOpen(
      this.snackBar.openFromComponent(SnackbarNewComponent, {
        duration: duration,
        panelClass: ["snackbar-new-panel"],
        data: { heading, message } as SnackbarNewData,
      })
    );
  }

  openSnackBar(
    message: string,
    duration: number = 5000,
    type: SnackbarType = "error"
  ) {
    this.trackOpen(
      this.snackBar.openFromComponent(SnackbarCustomComponent, {
        duration: duration,
        data: {
          message: message,
          type: type,
        },
      })
    );
  }

  /**
   * New-UI snackbar that stays open until manually dismissed via its close icon -
   * omitting `duration` from the MatSnackBar config entirely (rather than passing
   * a large number) is what makes it persistent.
   */
  openPersistentDesignSnackbar(heading: string, message: string) {
    return this.trackOpen(
      this.snackBar.openFromComponent(SnackbarNewComponent, {
        panelClass: ["snackbar-new-panel"],
        data: { heading, message } as SnackbarNewData,
      })
    );
  }

  /**
   * New-UI snackbar with an action button (e.g. "Undo").
   */
  openDesignSnackbarWithAction(
    heading: string,
    message: string,
    actionText: string,
    duration: number = 5000
  ) {
    return this.trackOpen(
      this.snackBar.openFromComponent(SnackbarNewComponent, {
        duration,
        panelClass: ["snackbar-new-panel"],
        data: { heading, message, actionText } as SnackbarNewData,
      })
    );
  }

  openSnackBarWithAction(
    message: string,
    actionText: string,
    duration: number = 5000,
    type: SnackbarType = "success"
  ) {
    const snackBarRef = this.snackBar.openFromComponent(
      SnackbarCustomComponent,
      {
        duration: duration,
        data: {
          message: message,
          actionText: actionText,
          type: type,
        },
      }
    );

    return this.trackOpen(snackBarRef);
  }

  /**
   * Onboarding snackbar (workbook Components sheet "Actionless snackbar", node 20:5882) - used
   * by H03, S04, S06B, DM03, FU05, MP05. Contract (FLIP-342 / FLIP-346): shows for 6s (pausing on
   * hover/keyboard focus - OnboardingSnackbarComponent runs that timer, so no MatSnackBar
   * `duration` here), aria-live polite, and queues FIFO behind any snackbar already open rather
   * than replacing it the way MatSnackBar normally would. `options.above` sits it just above a
   * coach-mark target (see OnboardingSnackbarComponent).
   *
   * Returns nothing: a queued snackbar has no ref until it actually opens.
   */
  openOnboardingSnackbar(heading: string, message: string, options: OnboardingSnackbarOptions = {}): void {
    const { duration = 6000, above, messageSecondary, onDismissed } = options;
    // .onboarding-snackbar-panel also lifts it clear of the mobile bottom nav (material-theme.scss).
    const panelClass = ["snackbar-new-panel", "onboarding-snackbar-panel"];
    this.onboardingQueue.push(() => {
      const ref = this.trackOpen(
        this.snackBar.openFromComponent(OnboardingSnackbarComponent, {
          politeness: "polite",
          panelClass,
          data: {
            heading,
            message,
            ...(messageSecondary ? { messageSecondary } : {}),
            above,
            duration,
          } as OnboardingSnackbarData,
        })
      );
      if (onDismissed) {
        ref.afterDismissed().pipe(take(1)).subscribe(() => onDismissed());
      }
    });
    this.syncBlockingSignal();
    this.pumpOnboardingQueue();
  }

  /**
   * Opens the next queued onboarding snackbar once nothing is on screen. Checks MatSnackBar's own
   * `_openedSnackBarRef` (not just refs this service opened) so it also waits behind snackbars
   * opened by the call sites that inject MatSnackBar directly. If another snackbar replaces the
   * one being waited on, that one is waited on in turn.
   */
  private pumpOnboardingQueue(): void {
    if (!this.onboardingQueue.length || this.waitingOnRef) return;
    const openRef = this.snackBar._openedSnackBarRef;
    if (openRef) {
      this.waitingOnRef = openRef;
      openRef.afterDismissed().subscribe(() => {
        this.waitingOnRef = null;
        this.pumpOnboardingQueue();
      });
      return;
    }
    const openNext = this.onboardingQueue.shift()!;
    openNext();
    this.syncBlockingSignal();
    // Anything still queued now waits behind the one just opened.
    this.pumpOnboardingQueue();
  }

  /**
   * FLIP-321 - feeds `BlockingUiSignalService` so the coach-mark engine (FLIP-303) knows a
   * snackbar is currently visible. Only covers snackbars opened through this service; the
   * handful of call sites that inject `MatSnackBar` directly aren't tracked (see
   * blocking-ui-signal.service.ts). Reports open while the latest tracked snackbar is up or any
   * onboarding snackbar is still queued, so later onboarding prompts wait for the queue to clear.
   * A replaced snackbar's (later) `afterDismissed` no longer clears the flag while its
   * replacement is still showing.
   */
  private trackOpen<T>(snackBarRef: MatSnackBarRef<T>): MatSnackBarRef<T> {
    this.currentRef = snackBarRef;
    this.syncBlockingSignal();
    snackBarRef.afterDismissed().subscribe(() => {
      if (this.currentRef === snackBarRef) this.currentRef = null;
      this.syncBlockingSignal();
    });
    return snackBarRef;
  }

  private syncBlockingSignal(): void {
    this.blockingUiSignal.setSnackbarOpen(this.currentRef !== null || this.onboardingQueue.length > 0);
  }
}
